// Server route-gas — the logged-out gas source, per PAIR.
//
// Gas follows the token's CONTRACT CODE, not the venue: a jetton with custom
// wallet code burns differently from the standard family on the same venue.
// The api keys every emulator measurement by pool and by the token's wallet
// code hash, and GET /v1/trade-gas?pair_address=… answers for ONE pair in
// strict honesty order (own pool → same code hash → nothing) — queueing an
// on-demand reference emulation when it has no answer yet. This module asks
// per pair, retries briefly while the server is warming, and exposes the
// figures to React. Nothing here is invented: a side the emulator never
// measured stays null.

import { useEffect, useRef, useSyncExternalStore } from 'react'
import { apiFetch } from '../api/client'

type RouteGasWire = { network_fee_nano: string; venue?: string; measured_at?: number } | null
type PairGasWire = { buy: RouteGasWire; sell: RouteGasWire; warming?: boolean }
type GlobalGasWire = {
  routes?: Array<{ venue: string; side: string; network_fee_nano: string; measured_at: number }>
}

type PairGasState = {
  buy: string | null
  sell: string | null
  fetchedAt: number
  attempt: number
  timer: number | null
  inflight: boolean
}

const NANO = /^[1-9]\d*$/
// The on-demand warm usually lands within seconds; stop asking after these.
const RETRY_MS = [3000, 6000, 12000, 24000]
const REFRESH_AFTER_MS = 60_000
const STORE_KEY = 'wils.trade.pair-gas.v1'
const VENUE_STORE_KEY = 'wils.trade.venue-gas.v2' // a localStorage key name, not a credential — gitleaks:allow
const STORE_MAX = 128
const STORE_FRESH_MS = 24 * 3600_000

const cache = new Map<string, PairGasState>()
// Bumped when a pair TRADES. It is part of the display pin's key, so a settled
// trade is the only thing that lets a shown figure be replaced.
const generations = new Map<string, number>()
// Venue-level figures (server-measured, newest per venue+side): the instant
// first-frame fallback for a pair this browser has never asked about. The
// per-pair answer refines it within one round trip.
const venueCache = new Map<string, { fee: string; at: number }>()
const listeners = new Set<() => void>()

// Instant on reopen: figures the server already gave this browser render with
// ZERO round trips (gas is a slow-moving measurement; a background refresh
// still fires on mount). Only server-measured values ever enter this store.
type StoredPairGas = { buy: string | null; sell: string | null; at: number }

function hydrateFromStorage(): void {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (!raw) return
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return
    for (const [pair, value] of Object.entries(parsed as Record<string, StoredPairGas>)) {
      if (!value || typeof value.at !== 'number' || Date.now() - value.at > STORE_FRESH_MS) continue
      const buy = typeof value.buy === 'string' && NANO.test(value.buy) ? value.buy : null
      const sell = typeof value.sell === 'string' && NANO.test(value.sell) ? value.sell : null
      if (buy === null && sell === null) continue
      // fetchedAt 0: serve instantly, refresh on the first mount anyway.
      cache.set(pair, { buy, sell, fetchedAt: 0, attempt: 0, timer: null, inflight: false })
    }
  } catch {
    // storage unavailable — first paint just waits one round trip.
  }
}

function persistToStorage(): void {
  try {
    const entries = [...cache.entries()]
      .filter(([, state]) => state.buy !== null || state.sell !== null)
      .map(
        ([pair, state]) =>
          [pair, { buy: state.buy, sell: state.sell, at: state.fetchedAt || Date.now() }] as const
      )
      .slice(-STORE_MAX)
    localStorage.setItem(STORE_KEY, JSON.stringify(Object.fromEntries(entries)))
  } catch {
    // storage unavailable — memory cache still serves this session.
  }
}

function hydrateVenuesFromStorage(): void {
  try {
    const raw = localStorage.getItem(VENUE_STORE_KEY)
    if (!raw) return
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return
    for (const [key, value] of Object.entries(
      parsed as Record<string, { fee: string; at: number }>
    )) {
      if (!value || typeof value.at !== 'number' || Date.now() - value.at > STORE_FRESH_MS) continue
      if (typeof value.fee !== 'string' || !NANO.test(value.fee)) continue
      venueCache.set(key, value)
    }
  } catch {
    // storage unavailable — first paint just waits one round trip.
  }
}

function persistVenuesToStorage(): void {
  try {
    localStorage.setItem(VENUE_STORE_KEY, JSON.stringify(Object.fromEntries(venueCache)))
  } catch {
    // storage unavailable — memory cache still serves this session.
  }
}

function noteVenueFigure(
  venue: string | undefined,
  side: 'buy' | 'sell',
  wire: RouteGasWire
): void {
  const fee = feeOf(wire)
  if (!venue || fee === null) return
  const at = typeof wire?.measured_at === 'number' ? wire.measured_at * 1000 : Date.now()
  const key = `${venue}|${side}`
  const current = venueCache.get(key)
  if (!current || at >= current.at) venueCache.set(key, { fee, at })
}

hydrateFromStorage()
hydrateVenuesFromStorage()

function notify(): void {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function feeOf(wire: RouteGasWire): string | null {
  const fee = wire?.network_fee_nano
  return typeof fee === 'string' && NANO.test(fee) ? fee : null
}

function fetchPair(pairAddress: string, attempt: number): void {
  const state = cache.get(pairAddress)
  if (state) {
    state.inflight = true
    state.timer = null
  }
  void apiFetch<PairGasWire>(`/v1/trade-gas?pair_address=${encodeURIComponent(pairAddress)}`)
    .then((wire) => {
      const next: PairGasState = {
        buy: feeOf(wire.buy),
        sell: feeOf(wire.sell),
        fetchedAt: Date.now(),
        attempt,
        timer: null,
        inflight: false,
      }
      // A side may still be warming server-side — ask again shortly,
      // a bounded number of times.
      if ((next.buy === null || next.sell === null) && attempt < RETRY_MS.length) {
        next.timer = window.setTimeout(() => fetchPair(pairAddress, attempt + 1), RETRY_MS[attempt])
      }
      noteVenueFigure(wire.buy?.venue, 'buy', wire.buy)
      noteVenueFigure(wire.sell?.venue, 'sell', wire.sell)
      cache.set(pairAddress, next)
      persistToStorage()
      persistVenuesToStorage()
      notify()
    })
    .catch(() => {
      // Absent server memory shows a dash, not an error; a later prime
      // (new mount after the refresh window) retries.
      cache.set(pairAddress, {
        buy: cache.get(pairAddress)?.buy ?? null,
        sell: cache.get(pairAddress)?.sell ?? null,
        fetchedAt: Date.now(),
        attempt,
        timer: null,
        inflight: false,
      })
    })
}

/** Ask the server about one pair — fired from an effect, never from render. */
export function primeServerRouteGas(pairAddress: string): void {
  if (!pairAddress) return
  const state = cache.get(pairAddress)
  if (state) {
    if (state.inflight || state.timer !== null) return
    // A hydrated-from-storage entry (fetchedAt 0) serves instantly but still
    // refreshes in the background; a recently fetched one does not re-ask.
    if (Date.now() - state.fetchedAt < REFRESH_AFTER_MS) return
  }
  cache.set(pairAddress, {
    buy: state?.buy ?? null,
    sell: state?.sell ?? null,
    fetchedAt: state?.fetchedAt ?? 0,
    attempt: 0,
    timer: null,
    inflight: true,
  })
  fetchPair(pairAddress, 0)
}

/**
 * A settled trade changes what the NEXT one costs on that pair: the jetton
 * wallet it just deployed is not deployed again, so the first buy's figure is
 * wrong for every buy after it. The operator watched 0.04 stay on screen after
 * a buy that had already made it 0.03, and it only corrected when they toggled
 * buy/sell — a re-render, not a refetch.
 *
 * The cached figure is kept while the new one is fetched: showing the previous
 * trade's cost for a moment is honest, blanking the panel is not.
 */
export function invalidateServerRouteGas(pairAddress: string): void {
  if (!pairAddress) return
  const state = cache.get(pairAddress)
  if (!state) return
  generations.set(pairAddress, (generations.get(pairAddress) ?? 0) + 1)
  if (state.timer !== null) window.clearTimeout(state.timer)
  cache.set(pairAddress, {
    buy: state.buy,
    sell: state.sell,
    fetchedAt: 0,
    attempt: 0,
    timer: null,
    inflight: false,
  })
  primeServerRouteGas(pairAddress)
}

// The same venue mapping the trade surface uses: tonfun pools route through
// the bigpump planner venue.
function plannerVenue(venue: string): string {
  return venue === 'tonfun' ? 'bigpump' : venue
}

let bootPrimed = false

/** One boot-time fetch of the server's whole route-gas memory: fills the
 * venue-level fallback while the user is still on the board, so the very
 * first pair page ever opened in this browser renders gas on frame one. */
export function primeServerRouteGasBoot(): void {
  if (bootPrimed) return
  bootPrimed = true
  void apiFetch<GlobalGasWire>('/v1/trade-gas')
    .then((wire) => {
      for (const route of wire.routes ?? []) {
        if (route.side !== 'buy' && route.side !== 'sell') continue
        noteVenueFigure(route.venue, route.side, route)
      }
      persistVenuesToStorage()
      notify()
    })
    .catch(() => {
      bootPrimed = false // a later mount may retry
    })
}

/** Last server-measured network fee for this pair and side. Falls back to
 * the venue's newest figure so gas is on screen from the FIRST frame.
 *
 * The value is PINNED per mount: the first figure shown never swaps to a
 * different one mid-view — a number visibly changing when the user did
 * nothing reads as a glitch (operator). The refined per-pair figure is in
 * the cache and shows from the next mount on; an exact typed-amount plan
 * still overrides this fallback entirely (useStableTradeMetrics). */
export function useServerRouteGas(
  pairAddress: string,
  venue: string,
  side: 'buy' | 'sell'
): string | null {
  useEffect(() => primeServerRouteGas(pairAddress), [pairAddress])
  const live = useSyncExternalStore(
    subscribe,
    () =>
      cache.get(pairAddress)?.[side] ??
      venueCache.get(`${plannerVenue(venue)}|${side}`)?.fee ??
      null
  )
  // The pin exists so the figure cannot change under the cursor of someone
  // about to trade, and that stays. But it also meant the number could ONLY
  // change by changing the key: after a buy deployed the jetton wallet the
  // panel still showed what the deployment had cost, and toggling buy/sell was
  // the only thing that corrected it — a key change, not a refresh.
  //
  // A settled trade on this pair is the one event that genuinely invalidates
  // the figure, so it — and nothing else — releases the pin. Background
  // refreshes still cannot swap a shown number.
  const pinned = useRef<{ key: string; value: string } | null>(null)
  const key = `${pairAddress}|${side}|${generations.get(pairAddress) ?? 0}`
  if (pinned.current !== null && pinned.current.key !== key) pinned.current = null
  if (pinned.current === null && live !== null) pinned.current = { key, value: live }
  return pinned.current?.value ?? null
}

/** Test-only: re-run the module-load hydration against current storage. */
export function hydrateServerRouteGasForTests(): void {
  hydrateFromStorage()
  hydrateVenuesFromStorage()
}

/** Test-only: forget everything and cancel pending retries. */
export function resetServerRouteGasForTests(): void {
  generations.clear()
  for (const state of cache.values()) {
    if (state.timer !== null) window.clearTimeout(state.timer)
  }
  cache.clear()
  venueCache.clear()
  bootPrimed = false
  try {
    localStorage.removeItem(STORE_KEY)
    localStorage.removeItem(VENUE_STORE_KEY)
  } catch {
    // storage unavailable in this test environment
  }
}
